<script setup>
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import {reactive, ref, watch} from "vue";
import aiPromptsService from "@/services/ai-prompts.service";
import useForm from "@/composables/useForm";

const servicesNames = {
    gpt: 'GPT',
    gemini: 'Gemini',
    ppx: 'Perplexity',
}

const showModal = ref(false);
const id = ref(null);
const currentTab = ref(null);

const prompt = ref(null);
const services = ref([]);

const selectedServices = reactive({
    gpt: false,
    gemini: false,
    ppx: false,
});

const {resetForm} = useForm(selectedServices);

const loading = reactive({
    gpt: false,
    gemini: false,
    ppx: false,
});

watch(() => selectedServices, (value) => {
    Object.keys(value).forEach(service => {
        if (value[service]) {
            if (!services.value.find(item => item.name === service)) {

                let serviceItem = {
                    name: service,
                    userMessage: prompt.value.user_message,
                    response: '',
                }

                if (prompt.value.system_message?.length && (service === 'gpt' || service === 'ppx')) {
                    serviceItem.systemMessage = prompt.value.system_message;
                }

                services.value.push(serviceItem);
            }
        } else {
            const index = services.value.findIndex(item => item.name === service);

            if (index > -1) {
                services.value.splice(index, 1);
            }
        }
    });

    currentTab.value = services.value[0]?.name || null;
}, {deep: true, immediate: true});

async function getData() {
    const {data} = await aiPromptsService.getEdit(id.value);
    prompt.value = data;
}

async function run(serviceData) {
    loading[serviceData.name] = true;
    const {data} = await aiPromptsService.run(serviceData);
    services.value.find(item => item.name === serviceData.name).response = data;

    loading[serviceData.name] = false;
}

function open(itemId) {
    id.value = itemId;
    resetForm();
    prompt.value = null;
    showModal.value = true;
    getData();
}

defineExpose({open});
</script>

<template>
    <b-modal
        v-model="showModal"
        modal-class="modal-w-xl" :title="`Run ${prompt?.name}`"
        hide-footer
    >
        <form v-if="prompt" class="form pb-5">
            <div v-for="(selected, name) in selectedServices" :key="name" class="form__item input-checkbox">
                <input :id="'selected-' + name" type="checkbox" v-model="selectedServices[name]">
                <label :for="'selected-' + name">{{servicesNames[name]}}</label>
            </div>

            <div>
                <ul v-if="services.length" class="nav nav-tabs">
                    <li v-for="service in services" :key="service.name" class="nav-item">
                        <a class="nav-link" :class="{'active': currentTab === service.name}" @click="currentTab = service.name">{{servicesNames[service.name]}}</a>
                    </li>
                </ul>

                <div v-for="service in services" :key="service.name">
                    <div v-if="currentTab === service.name">
                        <div class="form__item">
                            <h5 class="heading5 heading5--secondary">User Message</h5>

                            <textarea
                                v-model="service.userMessage"
                                rows="10"
                                class="form-control"
                            />
                        </div>

                        <div v-if="service.systemMessage?.length" class="form__item">
                            <h5 class="heading5 heading5--secondary">System Message</h5>

                            <textarea
                                v-model="service.systemMessage"
                                rows="10"
                                class="form-control"
                            />
                        </div>

                        <div class="modal-footer">
                            <PrimaryButton
                                :caption="`Run ${servicesNames[service.name]}`"
                                :loading-caption="`Running ${servicesNames[service.name]}...`"
                                :loading="loading[service.name]"
                                @click="run(service)"
                            />
                        </div>

                        <div class="form__item">
                            <h5 class="heading5 heading5--secondary">Response</h5>

                            <div class="form__item">
                                {{service.response}}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </b-modal>
</template>

<style scoped>

</style>
